import React, {
  useCallback,
  useEffect,
  useRef,
  useState
} from "react"

import {
  Link,
  useLocation
} from "react-router-dom"

import Cookies from "js-cookie"
import axios from "axios"

const API = "https://food-backend-9733.onrender.com"

const statusContent = {
  pending: {
    title: "Order Placed",
    text: "Your order was placed successfully."
  },
  confirmed: {
    title: "Order Confirmed",
    text: "We confirmed your homemade food order."
  },
  preparing: {
    title: "Preparing Order",
    text: "Your fresh food is being prepared."
  },
  "out for delivery": {
    title: "Out For Delivery",
    text: "Your order is on the way."
  },
  delivered: {
    title: "Delivered",
    text: "Your order has been delivered."
  },
  cancelled: {
    title: "Order Cancelled",
    text: "This order was cancelled."
  }
}

const Navbar = () => {
  const location = useLocation()

  const loginData = JSON.parse(
    Cookies.get("logincrd") || "{}"
  )

  const token = loginData?.token
  const user = loginData?.user

  const [menuOpen, setMenuOpen] = useState(false)
  const [notificationOpen, setNotificationOpen] = useState(false)
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [cartCount, setCartCount] = useState(0)

  const menuRef = useRef()

  const notificationUserKey =
    user?.id || user?.email || user?.phone || "guest"

  const seenKey =
    `srilekha-notifications-${notificationUserKey}`

  const seenReadyKey =
    `${seenKey}-ready`

  const getSeenNotifications = useCallback(() => {
    try {
      return JSON.parse(
        localStorage.getItem(seenKey) || "[]"
      )
    } catch {
      return []
    }
  }, [seenKey])

  const saveSeenNotifications = useCallback((ids) => {
    const existing = getSeenNotifications()

    const merged = Array.from(
      new Set([
        ...existing,
        ...ids
      ])
    )

    localStorage.setItem(
      seenKey,
      JSON.stringify(merged)
    )

    localStorage.setItem(seenReadyKey, "1")
  }, [getSeenNotifications, seenKey, seenReadyKey])

  const loadNotifications = useCallback(() => {
    if (!token) {
      setNotifications([])
      setUnreadCount(0)
      return
    }

    axios.get(`${API}/orders`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
    .then(res => {
      const nextNotifications = res.data
        .slice(0, 8)
        .map(order => {
          const status =
            order.order_status || "pending"

          const content =
            statusContent[status] ||
            statusContent.pending

          return {
            id: `${order.id}-${status}`,
            orderId: order.id,
            status,
            title: content.title,
            text: content.text,
            total: order.total,
            createdAt: order.created_at
          }
        })

      setNotifications(nextNotifications)

      const notificationIds =
        nextNotifications.map(item => item.id)

      const seenReady =
        localStorage.getItem(seenReadyKey) === "1"

      if (!seenReady) {
        saveSeenNotifications(notificationIds)
        setUnreadCount(0)
        return
      }

      const seen = getSeenNotifications()

      setUnreadCount(
        nextNotifications.filter(item =>
          !seen.includes(item.id)
        ).length
      )
    })
    .catch(err => console.log(err))
  }, [
    token,
    seenReadyKey,
    getSeenNotifications,
    saveSeenNotifications
  ])

  const loadCartCount = useCallback(() => {
    if (!token) {
      setCartCount(0)
      return
    }

    axios.get(`${API}/cart`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
    .then(res => {
      const count = res.data.reduce(
        (sum, item) =>
          sum + Number(item.quantity || 0),
        0
      )

      setCartCount(count)
    })
    .catch(err => console.log(err))
  }, [token])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target)
      ) {
        setMenuOpen(false)
        setNotificationOpen(false)
      }
    }

    document.addEventListener(
      "mousedown",
      handleClickOutside
    )

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      )
    }
  }, [])

  useEffect(() => {
    if (!token) {
      setCartCount(0)
      setNotifications([])
      setUnreadCount(0)
      return
    }

    loadCartCount()
    loadNotifications()
  }, [
    token,
    location.pathname,
    loadCartCount,
    loadNotifications
  ])

  useEffect(() => {
    window.addEventListener(
      "srilekha-cart-updated",
      loadCartCount
    )

    window.addEventListener(
      "srilekha-orders-updated",
      loadNotifications
    )

    return () => {
      window.removeEventListener(
        "srilekha-cart-updated",
        loadCartCount
      )

      window.removeEventListener(
        "srilekha-orders-updated",
        loadNotifications
      )
    }
  }, [loadCartCount, loadNotifications])

  const toggleNotifications = () => {
    const nextOpen = !notificationOpen

    setNotificationOpen(nextOpen)
    setMenuOpen(false)

    if (nextOpen) {
      loadNotifications()

      saveSeenNotifications(
        notifications.map(item => item.id)
      )

      setUnreadCount(0)
    }
  }

  return (
    <>
      <nav className="premium-navbar">
        <Link
          to="/"
          className="nav-left-box nav-brand-link"
          onClick={() => {
            setMenuOpen(false)
            setNotificationOpen(false)
          }}
        >
          <img
            src="/logo192.png"
            alt="logo"
            className="navbar-logo"
          />

          <h2>Srilekha - వంటిల్లు</h2>
        </Link>

        <div
          className="nav-right-top"
          ref={menuRef}
        >
          {token ? (
            <>
              <button
                type="button"
                className="notification-btn-top"
                onClick={toggleNotifications}
              >
                <img
                  src="/icons/bell.png"
                  alt="Notifications"
                  className="top-nav-icon"
                />

                {unreadCount > 0 && (
                  <span className="notification-dot">
                    {unreadCount}
                  </span>
                )}
              </button>

              {notificationOpen && (
                <div className="notification-dropdown">
                  <div className="notification-head">
                    <h3>Notifications</h3>
                    <span>{notifications.length}</span>
                  </div>

                  {notifications.length === 0 ? (
                    <div className="notification-empty">
                      No order updates yet.
                    </div>
                  ) : (
                    notifications.map(item => (
                      <Link
                        to="/orders"
                        key={item.id}
                        className="notification-item"
                        onClick={() =>
                          setNotificationOpen(false)
                        }
                      >
                        <span
                          className={`notification-status ${item.status.replaceAll(" ", "-")}`}
                        />

                        <div>
                          <h4>{item.title}</h4>
                          <p>{item.text}</p>
                          <small>
                            Order #{item.orderId} - ₹ {item.total}
                          </small>
                        </div>
                      </Link>
                    ))
                  )}
                </div>
              )}

              <button
                className="menu-btn-top"
                onClick={() => {
                  setMenuOpen(!menuOpen)
                  setNotificationOpen(false)
                }}
              >
                <img
                  src="/icons/menu.png"
                  alt="Menu"
                  className="top-nav-icon"
                />
              </button>

              {menuOpen && (
                <div className="top-menu-dropdown">
                  <MenuItem
                    to="/profile"
                    icon="/icons/profile.png"
                    text="Profile"
                    closeMenu={() => setMenuOpen(false)}
                  />

                  <MenuItem
                    to="/orders"
                    icon="/icons/orders.png"
                    text="Orders"
                    closeMenu={() => setMenuOpen(false)}
                  />

                  <MenuItem
                    to="/checkout"
                    icon="/icons/cart.png"
                    text="Checkout"
                    closeMenu={() => setMenuOpen(false)}
                  />

                  {user?.role === "admin" && (
                    <>
                      <MenuItem
                        to="/admin"
                        icon="/icons/admin.png"
                        text="Admin Panel"
                        closeMenu={() => setMenuOpen(false)}
                      />

                      <MenuItem
                        to="/admin/orders"
                        icon="/icons/orders.png"
                        text="Admin Orders"
                        closeMenu={() => setMenuOpen(false)}
                      />

                      <MenuItem
                        to="/admin/requirements"
                        icon="/icons/orders.png"
                        text="Custom Requests"
                        closeMenu={() => setMenuOpen(false)}
                      />

                      <MenuItem
                        to="/admin/dashboard"
                        icon="/icons/dashboard.png"
                        text="Dashboard"
                        closeMenu={() => setMenuOpen(false)}
                      />
                    </>
                  )}

                  <MenuItem
                    to="/logout"
                    icon="/icons/logout.png"
                    text="Logout"
                    closeMenu={() => setMenuOpen(false)}
                  />
                </div>
              )}
            </>
          ) : (
            <div className="guest-nav-box">
              <Link
                to="/"
                className="guest-home-btn"
              >
                Home
              </Link>

              <Link
                to="/login"
                className="top-auth-btn"
              >
                Login
              </Link>

              <Link
                to="/reg"
                className="top-auth-btn signup-btn"
              >
                Sign Up
              </Link>
            </div>
          )}
        </div>
      </nav>

      {token && (
        <div className="bottom-mobile-nav">
          <Link
            to="/"
            className={
              location.pathname === "/"
                ? "bottom-nav-item active-bottom-nav"
                : "bottom-nav-item"
            }
          >
            <img
              src="/icons/home.png"
              alt="Home"
            />

            <p>Home</p>
          </Link>

          <Link
            to="/cart"
            className={
              location.pathname === "/cart"
                ? "bottom-nav-item active-bottom-nav"
                : "bottom-nav-item"
            }
          >
            <img
              src="/icons/cart.png"
              alt="Cart"
            />

            {cartCount > 0 && (
              <span className="bottom-cart-badge">
                {cartCount > 99 ? "99+" : cartCount}
              </span>
            )}

            <p>Cart</p>
          </Link>

          <Link
            to="/orders"
            className={
              location.pathname === "/orders"
                ? "bottom-nav-item active-bottom-nav"
                : "bottom-nav-item"
            }
          >
            <img
              src="/icons/orders.png"
              alt="Orders"
            />

            <p>Orders</p>
          </Link>
        </div>
      )}
    </>
  )
}

const MenuItem = ({
  to,
  text,
  icon,
  closeMenu
}) => (
  <Link
    to={to}
    onClick={closeMenu}
    className="dropdown-menu-item"
  >
    <img
      src={icon}
      alt={text}
      className="dropdown-icon"
    />

    {text}
  </Link>
)

export default Navbar